import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Table,
  Button,
  Space,
  Tag,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  Tooltip,
  Modal,
  Spin,
  Select,
  Skeleton,
} from "antd";
import {
  PlusOutlined,
  PlayCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  DataScienceJobApi,
  DataScienceJob,
  DataScienceJobOrigin,
  TaskStatus,
} from "../../api/DataScienceJobApi";
import {
  RecommendationJobSelection,
  RecommendationRunApi,
} from "../../api/RecommendationRunApi";
import { getOrganizations } from "../../api/Services";
import { getApiErrorMessage } from "../../helpers/ApiErrorHelper";
import { getSelectedOrganizationId, hasWorkingOrganization } from "../../api/Helper";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import AdminValidation from "../../components/adminvalidation/AdminValidation";
import {
  getDataScienceJobTypes,
  isProvisionedSingletonSystemJob,
  isRecommendationJobType,
} from "./dataScienceJobTypes";
import { renderDataScienceStatusTag } from "./dataScienceStatusTag";
import { formatDuration, formatDurationSeconds } from "./formatDuration";
import {
  downloadTaskLogsFromServer,
  TASK_LOGS_MODAL_WIDTH,
  taskLogsModalBodyStyle,
  taskLogsModalStyle,
  taskLogsScrollContainerStyle,
} from "./taskLogsHelper";
import TaskLogLines, { canViewTaskLogs } from "./TaskLogLines";
import TaskLogsModalTitle from "./TaskLogsModalTitle";
import styles from "./DataScienceJobsTab.module.scss";

interface DataScienceJobsTabProps {
  jobType: string;
  jobOrigin: DataScienceJobOrigin;
  displayName: string;
  description: string;
}

const DataScienceJobsTab: React.FC<DataScienceJobsTabProps> = ({
  jobType,
  displayName,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSingletonSystemJob =
    getDataScienceJobTypes(t).find((item) => item.jobType === jobType)
      ?.singletonSystemJob === true;
  const [jobs, setJobs] = useState<DataScienceJob[]>([]);
  const [tasks, setTasks] = useState<TaskStatus[]>([]);
  const [selectedRecommendationJob, setSelectedRecommendationJob] =
    useState<RecommendationJobSelection | null>(null);
  const [loading, setLoading] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [organizations, setOrganizations] = useState<{ [key: number]: string }>(
    {}
  );

  const [logsModalVisible, setLogsModalVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string>("");
  const [selectedTaskJobLabel, setSelectedTaskJobLabel] = useState("");
  const [logs, setLogs] = useState<
    Array<{ timestamp: number; message: string }>
  >([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsDownloading, setLogsDownloading] = useState(false);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);

  const [jobsPagination, setJobsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [tasksPagination, setTasksPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    loadOrganizations();
    loadJobs();
    loadTasks();
    loadRecommendationSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobType]);

  const workingOrganizationId =
    hasWorkingOrganization() && getSelectedOrganizationId()
      ? Number(getSelectedOrganizationId())
      : undefined;

  useEffect(() => {
    if (!tasks.some((task) => task.status === "running")) {
      return;
    }

    const intervalId = window.setInterval(() => {
      loadJobs(jobsPagination.current, jobsPagination.pageSize);
      loadTasks(tasksPagination.current, tasksPagination.pageSize);
    }, 5000);

    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jobType,
    jobsPagination.current,
    jobsPagination.pageSize,
    tasks,
    tasksPagination.current,
    tasksPagination.pageSize,
    workingOrganizationId,
  ]);

  const loadOrganizations = async () => {
    try {
      const orgs = await getOrganizations();
      const orgMap: { [key: number]: string } = {};
      orgs.forEach((org) => {
        orgMap[org.id] = org.name;
      });
      setOrganizations(orgMap);
    } catch (error) {
      console.error("Error loading organizations:", error);
    }
  };

  const loadJobs = async (page = 1, pageSize = 10) => {
    if (!workingOrganizationId) {
      setJobs([]);
      setJobsPagination((prev) => ({ ...prev, current: page, pageSize, total: 0 }));
      return;
    }

    try {
      setLoading(true);
      const jobsData = await DataScienceJobApi.listJobs({
        organization_id: workingOrganizationId,
        job_type: jobType,
        page,
        pageSize,
      });

      let jobs: DataScienceJob[] = [];
      let total = 0;

      if (Array.isArray(jobsData)) {
        jobs = jobsData;
        total = jobsData.length;
      } else if (jobsData && typeof jobsData === "object") {
        jobs = jobsData.items || jobsData.data || jobsData.jobs || [];
        total = jobsData.total || 0;
      }

      setJobs(jobs);
      setJobsPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: total,
      }));
    } catch (error) {
      message.error(t("DataScienceJobs-LoadJobsError"));
    } finally {
      setLoading(false);
    }
  };

  const loadTasks = async (page = 1, pageSize = 10) => {
    if (!workingOrganizationId) {
      setTasks([]);
      setTasksPagination((prev) => ({ ...prev, current: page, pageSize, total: 0 }));
      setTasksLoading(false);
      return;
    }

    try {
      setTasksLoading(true);
      const tasksData = await DataScienceJobApi.listTasks({
        organization_id: workingOrganizationId,
        job_type: jobType,
        page,
        pageSize,
      });

      let rows: TaskStatus[] = [];
      let total = 0;
      if (Array.isArray(tasksData)) {
        rows = tasksData;
        total = tasksData.length;
      } else if (tasksData && typeof tasksData === "object") {
        rows = tasksData.items || tasksData.data || tasksData.tasks || [];
        total = tasksData.total || rows.length;
      }

      setTasks(rows);
      setTasksPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total,
      }));
    } catch (e) {
      message.error(t("DataScienceJobs-LoadTasksError"));
    } finally {
      setTasksLoading(false);
    }
  };

  const loadRecommendationSelection = async () => {
    if (!isRecommendationJobType(jobType) || !workingOrganizationId) {
      setSelectedRecommendationJob(null);
      return;
    }

    try {
      const selections = await RecommendationRunApi.getJobSelections(workingOrganizationId);
      setSelectedRecommendationJob(
        selections.find((selection) => selection.job_type === jobType) || null
      );
    } catch (error) {
      message.error(t("DataScienceJobs-LoadRecommendationSelectionError"));
    }
  };

  const handleSetRecommendationJob = async (job: DataScienceJob) => {
    try {
      setLoading(true);
      const selection = await RecommendationRunApi.setJobSelection(
        job.job_type,
        job.id,
        workingOrganizationId
      );
      setSelectedRecommendationJob(selection);
      message.success(t("DataScienceJobs-SetRecommendationSelectionSuccess"));
    } catch (error: any) {
      const backendMessage =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.message;
      message.error(
        backendMessage || t("DataScienceJobs-SetRecommendationSelectionError")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClearRecommendationJob = async () => {
    try {
      setLoading(true);
      await RecommendationRunApi.clearJobSelection(jobType, workingOrganizationId);
      setSelectedRecommendationJob(null);
      message.success(t("DataScienceJobs-ClearRecommendationSelectionSuccess"));
    } catch (error: any) {
      const backendMessage =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.message;
      message.error(
        backendMessage || t("DataScienceJobs-ClearRecommendationSelectionError")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRecommendationJob = async (jobId: number | undefined) => {
    if (jobId == null) {
      await handleClearRecommendationJob();
      return;
    }
    const job = jobs.find((item) => item.id === jobId);
    if (job) {
      await handleSetRecommendationJob(job);
    }
  };

  const handleReconcileTask = async (taskId: string) => {
    try {
      setLoading(true);
      await DataScienceJobApi.reconcileTask(taskId);
      message.success(t("DataScienceJobs-ReconcileSuccess"));
      await loadTasks(tasksPagination.current, tasksPagination.pageSize);
    } catch (error) {
      message.error(t("DataScienceJobs-ReconcileError"));
    } finally {
      setLoading(false);
    }
  };

  const handleViewLogs = async (task: TaskStatus) => {
    setSelectedTaskId(task.task_id);
    setSelectedTaskJobLabel(
      task.job_type
        ? t(`DataScienceJobs-JobTypes.${task.job_type}`) || task.job_type
        : displayName
    );
    setLogsModalVisible(true);
    setLogs([]);
    setNextToken(undefined);
    await loadLogs(task.task_id);
  };

  const loadLogs = async (taskId: string, loadMore: boolean = false) => {
    try {
      setLogsLoading(true);
      const response = await DataScienceJobApi.getTaskLogs(
        taskId,
        loadMore ? nextToken : undefined,
        200
      );

      const hasMore =
        Boolean(response.nextToken) && response.events.length > 0;

      if (loadMore) {
        if (response.events.length > 0) {
          setLogs((prev) => [...prev, ...response.events]);
        }
      } else {
        setLogs(response.events);
      }
      setNextToken(hasMore ? response.nextToken : undefined);
    } catch (error) {
      message.error(t("DataScienceJobs-LoadLogsError"));
    } finally {
      setLogsLoading(false);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isNearBottom && nextToken && !logsLoading) {
      loadLogs(selectedTaskId, true);
    }
  };

  const formatLogTimestamp = (timestamp: number) => {
    return format(new Date(timestamp), "dd/MM/yyyy HH:mm:ss", { locale: es });
  };

  const handleDownloadLogs = async () => {
    if (!selectedTaskId) return;

    try {
      setLogsDownloading(true);
      await downloadTaskLogsFromServer(selectedTaskId);
      message.success(t("Common-DownloadSuccessMessage"));
    } catch (error) {
      message.error(t("DataScienceJobs-DownloadLogsError"));
    } finally {
      setLogsDownloading(false);
    }
  };

  const handleExecuteJob = async (job: DataScienceJob) => {
    try {
      await DataScienceJobApi.executeJob(job.id);
      message.success(t("DataScienceJobs-ExecuteJobSuccess"));
      loadJobs();
      await loadTasks(tasksPagination.current, tasksPagination.pageSize);
    } catch (error) {
      message.error(
        getApiErrorMessage(error, t("DataScienceJobs-ExecuteJobError"))
      );
    }
  };

  const handleCancelTask = async (taskId: string) => {
    try {
      setLoading(true);
      await DataScienceJobApi.cancelTask(taskId);
      message.success(t("DataScienceJobs-CancelTaskSuccess"));
      await loadJobs(jobsPagination.current, jobsPagination.pageSize);
      await loadTasks(tasksPagination.current, tasksPagination.pageSize);
    } catch (error) {
      message.error(t("DataScienceJobs-CancelTaskError"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (jobId: number) => {
    try {
      await DataScienceJobApi.deleteJob(jobId);
      message.success(t("DataScienceJobs-DeleteJobSuccess"));
      loadJobs();
    } catch (error) {
      message.error(
        getApiErrorMessage(error, t("DataScienceJobs-DeleteJobError"))
      );
    }
  };

  const getStatusTag = (status?: string) => renderDataScienceStatusTag(status, t);

  const renderJobDuration = (job: DataScienceJob) => {
    if (job.last_duration_seconds != null) {
      return formatDurationSeconds(job.last_duration_seconds);
    }
    if (job.last_started_at) {
      return formatDuration(
        job.last_started_at,
        job.last_completed_at,
        job.last_status === "running" ? t("DataScienceJobs-DurationRunning") : undefined
      );
    }
    return "-";
  };

  const columns = [
    {
      title: t("DataScienceJobs-JobName"),
      dataIndex: "job_name",
      key: "job_name",
      sorter: (a: DataScienceJob, b: DataScienceJob) =>
        a.job_name.localeCompare(b.job_name),
    },
    {
      title: t("DataScienceJobs-Organization"),
      dataIndex: "organization_id",
      key: "organization_id",
      render: (organizationId: number) =>
        organizations[organizationId] || `ID: ${organizationId}`,
    },
    {
      title: t("DataScienceJobs-IsActive"),
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? t("Common-Active") : t("Common-Inactive")}
        </Tag>
      ),
      filters: [
        { text: t("Common-Active"), value: true },
        { text: t("Common-Inactive"), value: false },
      ],
      onFilter: (value: string | number | boolean, record: DataScienceJob) =>
        record.is_active === value,
    },
    {
      title: t("DataScienceJobs-Schedule"),
      dataIndex: "cron_expression",
      key: "cron_expression",
      render: (cronExpression: string) => {
        if (!cronExpression) return t("DataScienceJobs-Unscheduled");

        if (cronExpression.includes(":")) {
          const intervalMatch = cronExpression.match(/\*\/(\d+)/);
          const hourMatch = cronExpression.match(/^(\d+)/);

          if (intervalMatch) {
            const interval = intervalMatch[1];
            const hour = hourMatch ? hourMatch[1] : "*";
            if (hour !== "*" && hour !== "0") {
              return t("DataScienceJobs-CronExpressions.EveryXMinutesAtHour", {
                interval,
                hour,
              });
            }
            return t("DataScienceJobs-CronExpressions.EveryXMinutes", {
              interval,
            });
          }
          return cronExpression;
        }

        const parts = cronExpression.trim().split(/\s+/);
        if (parts.length !== 6) return cronExpression;

        const [, minute, hour, dayOfMonth, month, dayOfWeek] = parts;

        const dayMap: { [key: string]: string } = {
          "0": "Dom",
          "1": "Lun",
          "2": "Mar",
          "3": "Mié",
          "4": "Jue",
          "5": "Vie",
          "6": "Sáb",
          "7": "Dom",
        };

        const formatTime = () => {
          if (hour === "*" && minute === "0")
            return t("DataScienceJobs-CronExpressions.EveryHourAtMinute0");
          if (hour === "*" && minute.startsWith("*/"))
            return t("DataScienceJobs-CronExpressions.EveryXMinutesPattern", {
              interval: minute.substring(2),
            });
          if (hour === "*")
            return t("DataScienceJobs-CronExpressions.EveryHourAtMinute", {
              minute,
            });
          return t("DataScienceJobs-CronExpressions.AtTime", {
            time: `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`,
          });
        };

        if (dayOfMonth === "*" && dayOfWeek === "?" && month === "*") {
          return `${t(
            "DataScienceJobs-CronExpressions.AllDays"
          )} ${formatTime()}`;
        }

        if (
          dayOfMonth !== "*" &&
          dayOfMonth !== "?" &&
          month === "*" &&
          dayOfWeek === "?"
        ) {
          return `${t("DataScienceJobs-CronExpressions.DayOfMonth", {
            day: dayOfMonth,
          })} ${formatTime()}`;
        }

        if (month !== "*") {
          return `${t("DataScienceJobs-CronExpressions.InMonth", {
            month,
            day: dayOfMonth,
          })} ${formatTime()}`;
        }

        if (dayOfMonth === "?" && dayOfWeek !== "*" && dayOfWeek !== "?") {
          const dias = dayOfWeek
            .split(",")
            .map((d) => dayMap[d] || d)
            .join(", ");
          return `${t("DataScienceJobs-CronExpressions.EveryWeekOn", {
            days: dias,
          })} ${formatTime()}`;
        }

        if (
          minute.startsWith("*/") &&
          hour === "*" &&
          dayOfMonth === "*" &&
          dayOfWeek === "?"
        ) {
          return t("DataScienceJobs-CronExpressions.EveryXMinutes", {
            interval: minute.substring(2),
          });
        }

        if (hour.startsWith("*/") && dayOfMonth === "*" && dayOfWeek === "?") {
          return t("DataScienceJobs-CronExpressions.EveryXHours", {
            interval: hour.substring(2),
            minute,
          });
        }

        if (hour.includes("-")) {
          const [start, end] = hour.split("-");
          return t("DataScienceJobs-CronExpressions.EveryHourBetween", {
            start,
            end,
            minute,
          });
        }

        if (hour.includes(",")) {
          const horas = hour
            .split(",")
            .map((h) => h.padStart(2, "0") + ":" + minute.padStart(2, "0"));
          return t("DataScienceJobs-CronExpressions.AllDaysAtTimes", {
            times: horas.join(", "),
          });
        }
        return cronExpression;
      },
    },
    {
      title: t("DataScienceJobs-LastStatus"),
      dataIndex: "last_status",
      key: "last_status",
      render: (_: any, record: DataScienceJob) => {
        return getStatusTag(record.last_status);
      },
    },
    {
      title: t("DataScienceJobs-ExecutionCount"),
      dataIndex: "execution_count",
      key: "execution_count",
      sorter: (a: DataScienceJob, b: DataScienceJob) =>
        a.execution_count - b.execution_count,
    },
    {
      title: t("DataScienceJobs-LastExecution"),
      dataIndex: "last_execution",
      key: "last_execution",
      render: (_: any, record: DataScienceJob) => {
        const date = record.last_execution;
        return date
          ? format(new Date(date), "dd/MM/yyyy HH:mm", { locale: es })
          : "-";
      },
    },
    {
      title: t("DataScienceJobs-Duration"),
      key: "duration",
      render: (_: any, record: DataScienceJob) => renderJobDuration(record),
    },
    {
      title: t("DataScienceJobs-NextExecution"),
      dataIndex: "next_execution",
      key: "next_execution",
      render: (_: any, record: DataScienceJob) => {
        const date = record.next_execution;
        return date
          ? format(new Date(date), "dd/MM/yyyy HH:mm", { locale: es })
          : "-";
      },
    },
    {
      title: t("DataScienceJobs-Actions"),
      key: "actions",
      render: (_: any, record: DataScienceJob) => {
        const runningTask = tasks.find(
          (task) =>
            task.status === "running" &&
            (task.bright_id === record.id || task.brightId === record.id)
        );

        return (
          <Space size="small">
          <Tooltip title={t("DataScienceJobs-Execute")}>
            <Popconfirm
              title={(() => {
                const warningKey = getDataScienceJobTypes(t).find(
                  (item) => item.jobType === record.job_type
                )?.executeWarningKey;
                if (!warningKey) {
                  return t("DataScienceJobs-ConfirmExecute");
                }
                return (
                  <div style={{ maxWidth: 320 }}>
                    <div>{t("DataScienceJobs-ConfirmExecute")}</div>
                    <div style={{ marginTop: 4, color: "#faad14" }}>
                      {t(warningKey)}
                    </div>
                  </div>
                );
              })()}
              onConfirm={() => handleExecuteJob(record)}
              okText={t("Common-Yes")}
              cancelText={t("Common-No")}
              disabled={Boolean(runningTask)}
            >
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                size="small"
                disabled={!record.is_active || Boolean(runningTask)}
              />
            </Popconfirm>
          </Tooltip>
          <Tooltip title={t("DataScienceJobs-View")}>
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() =>
                navigate(`/admin/datasciencejobs/${record.id}/view`)
              }
            />
          </Tooltip>
          {!isSingletonSystemJob && (
            <Tooltip title={t("DataScienceJobs-Edit")}>
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() =>
                  navigate(
                    `/admin/datasciencejobs/${record.job_type}/${record.id}/edit`
                  )
                }
              />
            </Tooltip>
          )}
          {(!isSingletonSystemJob ||
            !isProvisionedSingletonSystemJob(record)) && (
            <Tooltip title={t("DataScienceJobs-Delete")}>
              <Popconfirm
                title={t("DataScienceJobs-ConfirmDelete")}
                onConfirm={() => handleDeleteJob(record.id)}
                okText={t("Common-Yes")}
                cancelText={t("Common-No")}
              >
                <Button danger icon={<DeleteOutlined />} size="small" />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
        );
      },
    },
  ];

  const taskColumns = [
    {
      title: "Task ID",
      dataIndex: "execution_id",
      key: "execution_id",
      width: 120,
      render: (executionId?: string) => (
        <Tooltip title={executionId || "-"} placement="topLeft">
          <span
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100px",
              fontFamily: "monospace",
              fontSize: "11px",
            }}
          >
            {executionId ? executionId.split("/").pop() || executionId : "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: t("DataScienceJobs-JobName"),
      dataIndex: "job_name",
      key: "job_name",
      render: (_: any, record: TaskStatus) => {
        return record.job_name || "-";
      },
    },
    {
      title: t("DataScienceJobs-Organization"),
      dataIndex: "organization_id",
      key: "organization_id",
      render: (orgId?: number) =>
        orgId != null ? organizations[orgId] || `ID: ${orgId}` : "-",
    },
    {
      title: t("DataScienceJobs-Status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) => getStatusTag(status),
    },
    {
      title: t("DataScienceJobs-Message"),
      dataIndex: "message",
      key: "message",
      ellipsis: {
        showTitle: false,
      },
      render: (message?: string) => (
        <Tooltip title={message || "-"} placement="topLeft">
          <span
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "200px",
            }}
          >
            {message || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: t("DataScienceJobs-StartedAt"),
      dataIndex: "started_at",
      key: "started_at",
      render: (_: any, record: TaskStatus) => {
        const date = record.started_at || record.created_at;
        return date
          ? format(new Date(date), "dd/MM/yyyy HH:mm", { locale: es })
          : "-";
      },
    },
    {
      title: t("DataScienceJobs-CompletedAt"),
      dataIndex: "completed_at",
      key: "completed_at",
      render: (date?: string) =>
        date ? format(new Date(date), "dd/MM/yyyy HH:mm", { locale: es }) : "-",
    },
    {
      title: t("DataScienceJobs-Duration"),
      key: "duration",
      render: (_: any, record: TaskStatus) =>
        formatDuration(
          record.started_at || record.created_at,
          record.completed_at,
          record.status === "running" ? t("DataScienceJobs-DurationRunning") : undefined
        ),
    },
    {
      title: t("Common-Actions"),
      key: "actions",
      width: 240,
      align: "right" as const,
      render: (_: any, record: TaskStatus) => (
        <Space size="small" style={{ justifyContent: "flex-end", width: "100%" }}>
          {canViewTaskLogs(record) && (
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewLogs(record)}
              title={t("DataScienceJobs-ViewLogs")}
            >
              {t("DataScienceJobs-Logs")}
            </Button>
          )}
          {record.execution_id && record.status === "running" && (
            <Button
              size="small"
              icon={<SyncOutlined />}
              loading={loading}
              onClick={() => handleReconcileTask(record.task_id)}
              title={t("DataScienceJobs-ReconcileTask")}
              style={{ fontSize: "11px", padding: "2px 6px" }}
            >
              {t("DataScienceJobs-Reconcile")}
            </Button>
          )}
          {record.status === "running" && (
            <Popconfirm
              title={t("DataScienceJobs-ConfirmCancelTask")}
              onConfirm={() => handleCancelTask(record.task_id)}
              okText={t("Common-Yes")}
              cancelText={t("Common-No")}
            >
              <Button
                size="small"
                danger
                loading={loading}
                style={{ fontSize: "11px", padding: "2px 6px" }}
              >
                {t("Common-Cancel")}
              </Button>
            </Popconfirm>
          )}
          {!record.execution_id && record.status !== "running" && <span>-</span>}
        </Space>
      ),
    },
  ];

  const taskSkeletonRows = Array.from(
    { length: tasksPagination.pageSize },
    (_, index) => ({ task_id: `task-skeleton-${index}` })
  );

  const taskSkeletonColumns = [
    {
      title: "Task ID",
      key: "execution_id",
      width: 120,
      render: () => (
        <Skeleton.Input active size="small" style={{ width: 80, minWidth: 80 }} />
      ),
    },
    {
      title: t("DataScienceJobs-JobName"),
      key: "job_name",
      render: () => (
        <Skeleton.Input active size="small" style={{ width: 160, minWidth: 120 }} />
      ),
    },
    {
      title: t("DataScienceJobs-Organization"),
      key: "organization_id",
      render: () => (
        <Skeleton.Input active size="small" style={{ width: 72, minWidth: 72 }} />
      ),
    },
    {
      title: t("DataScienceJobs-Status"),
      key: "status",
      render: () => (
        <Skeleton.Button active size="small" style={{ width: 72, height: 22 }} />
      ),
    },
    {
      title: t("DataScienceJobs-Message"),
      key: "message",
      render: () => (
        <Skeleton.Input active size="small" style={{ width: 180, minWidth: 140 }} />
      ),
    },
    {
      title: t("DataScienceJobs-StartedAt"),
      key: "started_at",
      render: () => (
        <Skeleton.Input active size="small" style={{ width: 108, minWidth: 108 }} />
      ),
    },
    {
      title: t("DataScienceJobs-CompletedAt"),
      key: "completed_at",
      render: () => (
        <Skeleton.Input active size="small" style={{ width: 108, minWidth: 108 }} />
      ),
    },
    {
      title: t("DataScienceJobs-Duration"),
      key: "duration",
      render: () => (
        <Skeleton.Input active size="small" style={{ width: 72, minWidth: 72 }} />
      ),
    },
    {
      title: t("Common-Actions"),
      key: "actions",
      width: 240,
      align: "right" as const,
      render: () => (
        <div style={{ textAlign: "right" }}>
          <Skeleton.Button active size="small" style={{ width: 64 }} />
        </div>
      ),
    },
  ];

  const stats = {
    totalJobs: jobs.length,
    activeJobs: jobs.filter((job) => job.is_active).length,
    runningTasks: tasks.filter((task) => task.status === "running").length,
    failedTasks: tasks.filter((task) => task.status === "failed").length,
  };

  const displayJobs = isSingletonSystemJob
    ? jobs.filter(isProvisionedSingletonSystemJob)
    : jobs;

  const singletonJob = displayJobs[0];

  const visibleJobColumns = isSingletonSystemJob
    ? columns.filter(
        (column) =>
          !["cron_expression", "job_name", "organization_id"].includes(
            String(column.key)
          )
      )
    : columns;

  return (
    <AdminValidation>
      <div>
        <div
          style={{
            marginBottom: "24px",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {!isSingletonSystemJob && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/admin/datasciencejobs/${jobType}/create`)}
            >
              {t("DataScienceJobs-NewJob")}
            </Button>
          )}
        </div>

        <Row gutter={16} style={{ marginBottom: "24px" }}>
          {isSingletonSystemJob ? (
            <>
              <Col span={6}>
                <Card>
                  <Statistic
                    title={t("DataScienceJobs-LastExecution")}
                    value={
                      singletonJob?.last_execution
                        ? format(new Date(singletonJob.last_execution), "dd/MM/yyyy HH:mm", {
                            locale: es,
                          })
                        : "-"
                    }
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ fontSize: 16 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title={t("DataScienceJobs-NextExecution")}
                    value={
                      singletonJob?.next_execution
                        ? format(new Date(singletonJob.next_execution), "dd/MM/yyyy HH:mm", {
                            locale: es,
                          })
                        : "-"
                    }
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ fontSize: 16 }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic
                    title={t("DataScienceJobs-ExecutionCount")}
                    value={singletonJob?.execution_count ?? 0}
                    prefix={<ExclamationCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic
                    title={t("DataScienceJobs-Statuses.running")}
                    value={stats.runningTasks}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic
                    title={t("DataScienceJobs-Statuses.failed")}
                    value={stats.failedTasks}
                    prefix={<CloseCircleOutlined />}
                    valueStyle={{ color: "#cf1322" }}
                  />
                </Card>
              </Col>
            </>
          ) : (
            <>
              <Col span={6}>
                <Card>
                  <Statistic
                    title={t("DataScienceJobs-TotalJobs")}
                    value={stats.totalJobs}
                    prefix={<ExclamationCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title={t("Common-Active")}
                    value={stats.activeJobs}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: "#3f8600" }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title={t("DataScienceJobs-Statuses.running")}
                    value={stats.runningTasks}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title={t("DataScienceJobs-Statuses.failed")}
                    value={stats.failedTasks}
                    prefix={<CloseCircleOutlined />}
                    valueStyle={{ color: "#cf1322" }}
                  />
                </Card>
              </Col>
            </>
          )}
        </Row>

        {isRecommendationJobType(jobType) && (
          <Card
            title={t("DataScienceJobs-RecommendationSelection")}
            style={{ marginBottom: "24px" }}
          >
            <Select
              className={styles.recommendationSelectionSelect}
              placeholder={t("DataScienceJobs-SelectRecommendationJob")}
              value={selectedRecommendationJob?.job_id}
              loading={loading}
              onChange={handleSelectRecommendationJob}
              allowClear
            >
              {jobs.map((job) => (
                <Select.Option
                  key={job.id}
                  value={job.id}
                  disabled={!job.is_active}
                >
                  {job.job_name}
                  {organizations[job.organization_id]
                    ? ` - ${organizations[job.organization_id]}`
                    : ""}
                  {!job.is_active ? ` (${t("Common-Inactive")})` : ""}
                </Select.Option>
              ))}
            </Select>
          </Card>
        )}

        <Card
          title={
            isSingletonSystemJob
              ? t("DataScienceJobs-SingletonRunTitle")
              : displayName
          }
          style={{ marginBottom: "24px" }}
        >
          <Table
            columns={visibleJobColumns}
            dataSource={displayJobs}
            rowKey="id"
            loading={loading}
            pagination={{
              current: jobsPagination.current,
              pageSize: jobsPagination.pageSize,
              total: jobsPagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} ${t(
                  "Common-PaginationOf"
                )} ${total} ${t("Common-PaginationItems")}`,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (page, pageSize) => {
                loadJobs(page, pageSize);
              },
              onShowSizeChange: (current, size) => {
                loadJobs(1, size);
              },
              locale: {
                items_per_page: t("Common-PaginationPerPage"),
                jump_to: t("Common-PaginationGoTo"),
                page: t("Common-PaginationPage"),
              },
            }}
            locale={{
              emptyText: t("DataScienceJobs-NoJobsFound"),
            }}
          />
        </Card>

        <Card title={t("DataScienceJobs-ExecutionHistory")}>
          {tasksLoading && tasks.length === 0 ? (
            <Table
              columns={taskSkeletonColumns}
              dataSource={taskSkeletonRows}
              rowKey="task_id"
              scroll={{ x: 1100 }}
              pagination={false}
            />
          ) : (
            <Table
              columns={taskColumns}
              dataSource={tasks}
              rowKey="task_id"
              scroll={{ x: 1100 }}
              loading={tasksLoading}
              pagination={{
              current: tasksPagination.current,
              pageSize: tasksPagination.pageSize,
              total: tasksPagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} ${t(
                  "Common-PaginationOf"
                )} ${total} ${t("Common-PaginationItems")}`,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (page, pageSize) => {
                loadTasks(page, pageSize);
              },
              onShowSizeChange: (current, size) => {
                loadTasks(1, size);
              },
              locale: {
                items_per_page: t("Common-PaginationPerPage"),
                jump_to: t("Common-PaginationGoTo"),
                page: t("Common-PaginationPage"),
              },
            }}
            locale={{
              emptyText: t("DataScienceJobs-NoTasksFound"),
            }}
          />
          )}
        </Card>

        {/* Logs Modal */}
        <Modal
          title={
            <TaskLogsModalTitle
              title={`${t("DataScienceJobs-TaskLogs")}${selectedTaskJobLabel ? ` - ${selectedTaskJobLabel}` : ""} - ${selectedTaskId}`}
              onDownload={handleDownloadLogs}
              downloading={logsDownloading}
              downloadDisabled={!selectedTaskId}
              downloadTooltip={t("Common-Download")}
            />
          }
          visible={logsModalVisible}
          onCancel={() => setLogsModalVisible(false)}
          width={TASK_LOGS_MODAL_WIDTH}
          style={taskLogsModalStyle}
          bodyStyle={taskLogsModalBodyStyle}
          footer={[
            <Button key="close" onClick={() => setLogsModalVisible(false)}>
              {t("Common-Close")}
            </Button>,
          ]}
        >
          <div
            style={taskLogsScrollContainerStyle}
            onScroll={handleScroll}
          >
            <TaskLogLines
              logs={logs}
              logsLoading={logsLoading}
              nextToken={nextToken}
              formatTimestamp={formatLogTimestamp}
              t={t}
            />
          </div>
        </Modal>
      </div>
    </AdminValidation>
  );
};

export default DataScienceJobsTab;
